import { Minus, Plus } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { EtaInput, PartLineView } from "../../../../shared/contracts.js";
import type { CaseStatus, PartException } from "../../../../shared/domain.js";
import {
  caseReceiptEndpoints,
  mutationErrorMessage,
  usePreviewReceipt,
  useVerifyReceipt,
  type CaseMutationResultDto,
  type PreviewReceiptResult,
  type ReceiptEndpoints,
  type ReceiptLineInput,
} from "../../../api/caseMutations.js";
import { Button } from "../../../ui/Button.js";
import { Dialog, DialogActions } from "../../../ui/Dialog.js";
import { STATUS_PRESENTATION, StatusBadge } from "../../../ui/StatusBadge.js";
import { RecipientWarnings } from "./RecipientWarning.js";

/**
 * Verified arrival (spec §7): Parts enters the quantity received now for each
 * line, confirms the part numbers and quantities were physically verified,
 * sees exactly what will happen (partial receipt with no email, or Arrived
 * with the 48-hour timer and one email plus any recipient warnings), and
 * only then commits.
 *
 * Quantities are deltas clamped to what remains on each line — never below
 * zero, never above ordered minus verified. Preview is a read-only call; the
 * commit sends `confirmed: true` with the case version the dialog opened
 * with, so a change made elsewhere in between is refused as a stale write
 * instead of being overwritten.
 *
 * The version, status and lines are frozen when the dialog opens. The parent
 * may hand down a fresher `detail` while the modal is up (React Query
 * refetches on window focus, for one); if the dialog read the live props,
 * the preview on screen would silently describe a different receipt, and the
 * commit would carry the new version — so the server would accept a receipt
 * Parts never saw previewed (3 + 1 = 4 completes the line, Arrived, email).
 * Frozen, the commit is exactly what was previewed and the server's version
 * check refuses it with the stale-write message if the case moved on.
 *
 * The same dialog verifies a wholesale order (wholesale spec §4.5): handed
 * that order's `endpoints` it posts the same receipt to the wholesale
 * routes, and `side="wholesale"` swaps the outcome copy — the order moves
 * to the Arrived list for 36 hours and no email is sent (the customer
 * email is a separate, manual action).
 */

export const EXCEPTION_LABELS: Readonly<Record<PartException, string>> = {
  backordered: "Backordered",
  wrong_part: "Wrong part",
  damaged_part: "Damaged part",
  cancelled_line: "Cancelled line",
};

export function exceptionLabel(exception: PartException): string {
  return EXCEPTION_LABELS[exception] ?? exception;
}

const etaFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "No ETA" or the calendar date as Parts entered it (a date, not an instant). */
export function etaLabel(eta: EtaInput): string {
  if (eta.kind === "no_eta") return "No ETA";
  const [year, month, day] = eta.date.split("-").map(Number);
  if (!year || !month || !day) return eta.date;
  // Noon UTC keeps the calendar day stable in America/Chicago.
  return etaFormatter.format(new Date(Date.UTC(year, month - 1, day, 12)));
}

export function remainingQuantity(line: PartLineView): number {
  return Math.max(0, line.quantityOrdered - line.quantityVerified);
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, Math.trunc(value)));
}

export interface VerifyArrivalDialogProps<R = CaseMutationResultDto> {
  open: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  /** Which side's copy the preview reads; the requests follow `endpoints`. */
  side?: "service" | "wholesale";
  /** Where the preview and the receipt are posted; defaults to the case routes for `caseId`. */
  endpoints?: ReceiptEndpoints<R>;
  /**
   * The case version the inspector is showing when the dialog opens. Frozen
   * for the life of the dialog and sent with preview and commit.
   */
  version: number;
  /** Frozen at open, like `version`. */
  status: CaseStatus;
  /** Frozen at open, like `version`. */
  partLines: readonly PartLineView[];
  /**
   * The assigned technician's name, `null` for an unassigned slot (allowed
   * since 2026-09-17). Frozen at open, like `version`: the preview's
   * recipient sentence names the technician only when there is one — the
   * server sends no recipient warning for an empty slot, so nothing else on
   * screen would correct the promise.
   */
  technicianName: string | null;
  /** Called once the server has committed the receipt. */
  onVerified: (result: R) => void;
}

/** Mounts the flow only while open so every open starts from a clean form. */
export function VerifyArrivalDialog<R = CaseMutationResultDto>(props: VerifyArrivalDialogProps<R>) {
  if (!props.open) return null;
  return <VerifyArrivalFlow {...props} />;
}

type Step = "quantities" | "preview";

const CONFIRMATION_ERROR =
  "Confirm that you physically verified the part numbers and quantities before continuing.";
const EMPTY_RECEIPT_ERROR = "Enter a received quantity for at least one line.";

function VerifyArrivalFlow<R>({
  onClose,
  caseId,
  caseNumber,
  side = "service",
  endpoints,
  onVerified,
  ...live
}: VerifyArrivalDialogProps<R>) {
  // The case as it stood when the dialog opened (see the module comment).
  // `VerifyArrivalDialog` unmounts this flow on close, so every open snapshots
  // afresh.
  const [{ version, status, partLines, technicianName }] = useState(() => ({
    version: live.version,
    status: live.status,
    partLines: live.partLines,
    technicianName: live.technicianName,
  }));
  const [step, setStep] = useState<Step>("quantities");
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(partLines.map((line) => [line.id, 0])),
  );
  const [confirmed, setConfirmed] = useState(false);
  const [confirmationError, setConfirmationError] = useState<string | null>(
    null,
  );
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewReceiptResult | null>(null);

  // Without endpoints the dialog works the case routes, whose result is the default `R`.
  const [api] = useState(() => endpoints ?? (caseReceiptEndpoints(caseId) as unknown as ReceiptEndpoints<R>));
  const previewMutation = usePreviewReceipt(api);
  const verifyMutation = useVerifyReceipt(api);
  const pending = previewMutation.isPending || verifyMutation.isPending;

  const confirmationId = useId();
  const confirmationErrorId = useId();
  const receiptErrorId = useId();
  const continueRef = useRef<HTMLButtonElement>(null);
  const previewHeadingRef = useRef<HTMLHeadingElement>(null);

  // Moving between steps unmounts whatever had focus; put it somewhere
  // meaningful — the top of the preview, or back on Continue — instead of
  // letting it fall to the document body.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (step === "preview") previewHeadingRef.current?.focus();
    else continueRef.current?.focus();
  }, [step]);

  const openLines = useMemo(
    () => partLines.filter((line) => remainingQuantity(line) > 0),
    [partLines],
  );
  const completeLines = useMemo(
    () => partLines.filter((line) => remainingQuantity(line) === 0),
    [partLines],
  );

  const receipts: ReceiptLineInput[] = useMemo(
    () =>
      openLines
        .map((line) => ({
          partLineId: line.id,
          quantityReceivedNow: quantities[line.id] ?? 0,
        }))
        .filter((receipt) => receipt.quantityReceivedNow > 0),
    [openLines, quantities],
  );

  const setQuantity = (line: PartLineView, next: number) => {
    setQuantities((current) => ({
      ...current,
      [line.id]: clamp(next, 0, remainingQuantity(line)),
    }));
    setReceiptError(null);
  };

  const continueToPreview = () => {
    setSubmitError(null);
    let valid = true;
    if (!confirmed) {
      setConfirmationError(CONFIRMATION_ERROR);
      valid = false;
    }
    if (receipts.length === 0) {
      setReceiptError(EMPTY_RECEIPT_ERROR);
      valid = false;
    }
    if (!valid) return;

    previewMutation.mutate(
      { receipts, expectedVersion: version },
      {
        onSuccess: (result) => {
          setPreview(result);
          setStep("preview");
        },
        onError: (error) => setSubmitError(mutationErrorMessage(error)),
      },
    );
  };

  const commit = () => {
    setSubmitError(null);
    verifyMutation.mutate(
      { receipts, confirmed: true, expectedVersion: version },
      {
        onSuccess: (result) => {
          onVerified(result);
          onClose();
        },
        onError: (error) => setSubmitError(mutationErrorMessage(error)),
      },
    );
  };

  const goBack = () => {
    setSubmitError(null);
    setStep("quantities");
  };

  const currentStatusLabel = STATUS_PRESENTATION[status].label;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Verify arrival · ${caseNumber}`}
      description={
        step === "quantities"
          ? "Enter what was physically received for each line. Quantities add to what is already verified."
          : "Check what this receipt will do before saving it."
      }
      pending={pending}
      size="large"
    >
      {step === "quantities" ? (
        <>
          <ul className="receipt-lines" aria-label="Part lines">
            {openLines.map((line) => (
              <ReceiptLineRow
                key={line.id}
                line={line}
                value={quantities[line.id] ?? 0}
                onChange={(next) => setQuantity(line, next)}
                disabled={pending}
              />
            ))}
            {completeLines.map((line) => (
              <li key={line.id} className="receipt-line" data-complete="true">
                <LineIdentity line={line} />
                <p className="receipt-line__progress">
                  {line.quantityVerified} of {line.quantityOrdered} verified ·
                  Complete
                </p>
              </li>
            ))}
          </ul>
          {receiptError ? (
            <p id={receiptErrorId} role="alert" className="field__error">
              {receiptError}
            </p>
          ) : null}

          <div
            className="field receipt-confirmation"
            data-invalid={confirmationError ? "true" : undefined}
          >
            <label
              className="receipt-confirmation__label"
              htmlFor={confirmationId}
            >
              <input
                id={confirmationId}
                type="checkbox"
                className="receipt-confirmation__checkbox"
                checked={confirmed}
                disabled={pending}
                aria-describedby={
                  confirmationError ? confirmationErrorId : undefined
                }
                aria-invalid={confirmationError ? true : undefined}
                onChange={(event) => {
                  setConfirmed(event.target.checked);
                  if (event.target.checked) setConfirmationError(null);
                }}
              />
              <span>
                I physically verified the part numbers and quantities entered
                above.
              </span>
            </label>
            {confirmationError ? (
              <p id={confirmationErrorId} role="alert" className="field__error">
                {confirmationError}
              </p>
            ) : null}
          </div>

          {submitError ? (
            <p role="alert" className="form-alert">
              {submitError}
            </p>
          ) : null}

          <DialogActions>
            <Button variant="secondary" disabled={pending} onClick={onClose}>
              Cancel
            </Button>
            <Button
              ref={continueRef}
              variant="primary"
              busy={previewMutation.isPending}
              onClick={continueToPreview}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      ) : preview ? (
        <>
          <section className="receipt-summary" aria-label="This receipt">
            <h3
              ref={previewHeadingRef}
              tabIndex={-1}
              className="receipt-summary__title"
            >
              This receipt
            </h3>
            <ul className="receipt-summary__list">
              {receipts.map((receipt) => {
                const line = partLines.find(
                  (candidate) => candidate.id === receipt.partLineId,
                );
                if (!line) return null;
                const after =
                  line.quantityVerified + receipt.quantityReceivedNow;
                return (
                  <li
                    key={receipt.partLineId}
                    className="receipt-summary__item"
                  >
                    <span className="receipt-summary__part">
                      {line.partNumber}
                    </span>{" "}
                    <span className="receipt-summary__description">
                      {line.description}
                    </span>{" "}
                    <span className="receipt-summary__delta">
                      +{receipt.quantityReceivedNow} → {after} of{" "}
                      {line.quantityOrdered} verified
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="receipt-outcome" aria-label="What happens next">
            <div className="receipt-outcome__status">
              <span className="receipt-outcome__label">
                Order status after saving
              </span>
              <StatusBadge status={preview.projectedStatus} />
            </div>
            {preview.willArrive && side === "wholesale" ? (
              <p className="receipt-outcome__copy">
                Every ordered quantity will be verified. The order{" "}
                <strong>becomes Arrived</strong> and moves to the{" "}
                <strong>Arrived list for 36 hours</strong> for the desk to pull
                and hand over. No email is sent — use <strong>Email customer</strong>{" "}
                on the order when you want to let the customer know.
              </p>
            ) : preview.willArrive ? (
              <>
                <p className="receipt-outcome__copy">
                  Every ordered quantity will be verified. The order{" "}
                  <strong>becomes Arrived</strong>, its 48-hour dashboard timer
                  starts now, and <strong>one arrival email</strong> goes to the
                  assigned advisor
                  {technicianName !== null
                    ? ", the assigned technician"
                    : ""}{" "}
                  and every active service manager, shop foreman and dispatcher.
                </p>
                {technicianName === null ? (
                  <p className="receipt-outcome__copy">
                    No technician is assigned to this order.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="receipt-outcome__copy">
                This is a <strong>partial receipt</strong>. The order{" "}
                <strong>
                  stays {STATUS_PRESENTATION[preview.projectedStatus].label}
                </strong>
                {preview.projectedStatus !== status
                  ? ` (it was ${currentStatusLabel})`
                  : ""}
                . No email is sent — the website updates right away.
              </p>
            )}
          </section>

          <RecipientWarnings warnings={preview.recipientWarnings} />

          {submitError ? (
            <p role="alert" className="form-alert">
              {submitError}
            </p>
          ) : null}

          <DialogActions>
            <Button variant="secondary" disabled={pending} onClick={goBack}>
              Back
            </Button>
            <Button
              variant="primary"
              busy={verifyMutation.isPending}
              onClick={commit}
            >
              {preview.willArrive ? "Confirm arrival" : "Save receipt"}
            </Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}

function LineIdentity({ line }: { line: PartLineView }) {
  return (
    <div className="receipt-line__identity">
      <span className="receipt-line__part">{line.partNumber}</span>
      <span className="receipt-line__description">{line.description}</span>
      <span className="receipt-line__meta">
        {etaLabel(line.eta)}
        {line.exception ? (
          <>
            {" · "}
            <span className="receipt-line__exception">
              {exceptionLabel(line.exception)} — blocks Arrived until cleared
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}

interface ReceiptLineRowProps {
  line: PartLineView;
  value: number;
  onChange: (next: number) => void;
  disabled: boolean;
}

/** One open line: identity, progress and a clamped "received now" stepper. */
function ReceiptLineRow({
  line,
  value,
  onChange,
  disabled,
}: ReceiptLineRowProps) {
  const inputId = useId();
  const remainingId = useId();
  const remaining = remainingQuantity(line);

  return (
    <li className="receipt-line">
      <LineIdentity line={line} />
      <p id={remainingId} className="receipt-line__progress">
        {line.quantityVerified} of {line.quantityOrdered} verified · {remaining}{" "}
        remaining
      </p>
      <div className="quantity-stepper">
        <label htmlFor={inputId} className="quantity-stepper__label">
          Received now for {line.partNumber}
        </label>
        <div className="quantity-stepper__controls">
          <Button
            variant="secondary"
            iconOnly
            icon={<Minus />}
            disabled={disabled || value <= 0}
            onClick={() => onChange(value - 1)}
          >
            Decrease received quantity for {line.partNumber}
          </Button>
          <input
            id={inputId}
            type="number"
            inputMode="numeric"
            className="text-input quantity-stepper__input"
            data-size="desktop"
            min={0}
            max={remaining}
            step={1}
            value={value}
            disabled={disabled}
            aria-describedby={remainingId}
            onChange={(event) => {
              const clamped = clamp(event.target.valueAsNumber, 0, remaining);
              // React re-renders only when the clamped value changes, so a
              // typed "39" that clamps to an unchanged 3 must be written back
              // here or the field would keep showing what was refused.
              if (event.target.value !== String(clamped)) {
                event.target.value = String(clamped);
              }
              onChange(clamped);
            }}
          />
          <Button
            variant="secondary"
            iconOnly
            icon={<Plus />}
            disabled={disabled || value >= remaining}
            onClick={() => onChange(value + 1)}
          >
            Increase received quantity for {line.partNumber}
          </Button>
        </div>
      </div>
    </li>
  );
}
